/**
 * AlertsManager - Admin panel for managing all alerts
 * Features: Weather alerts, custom alerts, alert history, push notifications
 */

import { useState, useEffect } from 'react';
import { useAuth, API } from '@/App';
import { toast } from 'sonner';
import { 
  Bell, CloudRain, Send, Trash2, AlertTriangle, CloudLightning,
  Sun, Thermometer, Home, Users, User, MessageCircle, Trophy,
  Clock, CheckCircle, XCircle, Loader2, Plus, Power, History, BellRing, Smartphone
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import axios from 'axios';

const AlertsManager = () => {
  const { authAxios, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [weatherAlert, setWeatherAlert] = useState(null);
  const [alertHistory, setAlertHistory] = useState([]);
  const [users, setUsers] = useState([]);
  const [pushStats, setPushStats] = useState({ subscribers: 0 });
  
  // Push notification form
  const [pushForm, setPushForm] = useState({
    title: '',
    body: '',
    tag: 'announcement',
  });
  
  // Weather alert form
  const [weatherForm, setWeatherForm] = useState({
    alert_type: 'rain_delay',
    title: '',
    message: '',
    severity: 'warning',
  });
  
  // Custom alert form
  const [customForm, setCustomForm] = useState({
    alert_type: 'custom',
    title: '',
    message: '',
    severity: 'info',
    display_style: 'toast',
    target_users: [],
  });

  // Weather alert type presets
  const weatherPresets = {
    practice_cancelled: {
      title: '⛔ PRACTICE CANCELLED',
      message: 'Today\'s practice has been cancelled due to weather conditions. Stay safe!',
      severity: 'danger',
      icon: 'alert-triangle',
    },
    indoor_training: {
      title: '🏠 INDOOR TRAINING',
      message: 'Practice moved indoors due to weather. Check your messages for the new location.',
      severity: 'warning',
      icon: 'home',
    },
    heat_advisory: {
      title: '🌡️ HEAT ADVISORY',
      message: 'Extreme heat expected. Bring extra water and take frequent breaks.',
      severity: 'danger',
      icon: 'thermometer',
    },
    lightning: {
      title: '⚡ LIGHTNING DELAY',
      message: 'Practice delayed due to lightning in the area. Seek shelter immediately.',
      severity: 'danger',
      icon: 'cloud-lightning',
    },
    rain_delay: {
      title: '🌧️ RAIN DELAY',
      message: 'Practice delayed due to rain. We\'ll update you when conditions improve.',
      severity: 'warning',
      icon: 'cloud-rain',
    },
  };

  // Fetch data
  useEffect(() => {
    fetchWeatherAlert();
    fetchAlertHistory();
    fetchUsers();
  }, []);

  const fetchWeatherAlert = async () => {
    try {
      const res = await authAxios.get('/alerts/weather');
      setWeatherAlert(res.data);
    } catch (err) {
      console.error('Failed to fetch weather alert:', err);
    }
  };

  const fetchAlertHistory = async () => {
    try {
      const res = await authAxios.get('/alerts/history');
      setAlertHistory(res.data || []);
    } catch (err) {
      console.error('Failed to fetch alert history:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await authAxios.get('/admin/users');
      setUsers(res.data || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  // Apply weather preset
  const applyWeatherPreset = (type) => {
    const preset = weatherPresets[type];
    if (preset) {
      setWeatherForm({
        alert_type: type,
        title: preset.title,
        message: preset.message,
        severity: preset.severity,
      });
    }
  };

  // Create weather alert
  const createWeatherAlert = async (e) => {
    e.preventDefault();
    if (!weatherForm.title || !weatherForm.message) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setLoading(true);
    try {
      await authAxios.post('/alerts/weather', weatherForm);
      toast.success('Weather alert created!');
      fetchWeatherAlert();
    } catch (err) {
      toast.error('Failed to create weather alert');
    } finally {
      setLoading(false);
    }
  };

  // Toggle weather alert
  const toggleWeatherAlert = async () => {
    setLoading(true);
    try {
      const res = await authAxios.put('/alerts/weather/toggle');
      toast.success(res.data.message);
      fetchWeatherAlert();
    } catch (err) {
      toast.error('Failed to toggle weather alert');
    } finally {
      setLoading(false);
    }
  };

  // Delete weather alert
  const deleteWeatherAlert = async () => {
    setLoading(true);
    try {
      await authAxios.delete('/alerts/weather');
      toast.success('Weather alert removed');
      setWeatherAlert(null);
    } catch (err) {
      toast.error('Failed to delete weather alert');
    } finally {
      setLoading(false);
    }
  };

  // Create custom alert
  const createCustomAlert = async (e) => {
    e.preventDefault();
    if (!customForm.title || !customForm.message) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setLoading(true);
    try {
      await authAxios.post('/alerts', customForm);
      toast.success('Alert sent!');
      setCustomForm({
        alert_type: 'custom',
        title: '',
        message: '',
        severity: 'info',
        display_style: 'toast',
        target_users: [],
      });
      fetchAlertHistory();
    } catch (err) {
      toast.error('Failed to send alert');
    } finally {
      setLoading(false);
    }
  };

  // Delete alert from history
  const deleteAlert = async (alertId) => {
    try {
      await authAxios.delete(`/alerts/${alertId}`);
      toast.success('Alert deleted');
      fetchAlertHistory();
    } catch (err) {
      toast.error('Failed to delete alert');
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // Severity badge colors
  const severityColors = {
    info: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    success: 'bg-green-500/20 text-green-400 border-green-500/30',
    warning: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    danger: 'bg-red-500/20 text-red-400 border-red-500/30',
    celebration: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
  };

  return (
    <div className="space-y-6" data-testid="alerts-manager">
      <div className="flex items-center justify-between">
        <h2 className="font-orbitron text-xl text-neon-blue flex items-center gap-2">
          <Bell className="w-5 h-5" />
          ALERT MANAGEMENT
        </h2>
      </div>

      <Tabs defaultValue="weather" className="space-y-4">
        <TabsList className="bg-gray-800/50 border border-gray-700">
          <TabsTrigger value="weather" className="data-[state=active]:bg-neon-blue/20 data-[state=active]:text-neon-blue">
            <CloudRain className="w-4 h-4 mr-2" />
            Weather
          </TabsTrigger>
          <TabsTrigger value="custom" className="data-[state=active]:bg-neon-pink/20 data-[state=active]:text-neon-pink">
            <Send className="w-4 h-4 mr-2" />
            Send Alert
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-400">
            <History className="w-4 h-4 mr-2" />
            History
          </TabsTrigger>
        </TabsList>

        {/* WEATHER ALERTS TAB */}
        <TabsContent value="weather" className="space-y-4">
          {/* Current Weather Alert Status */}
          <div className={`p-4 rounded-lg border ${weatherAlert?.is_active ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-gray-800/50 border-gray-700'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${weatherAlert?.is_active ? 'bg-yellow-500/20' : 'bg-gray-700'}`}>
                  <CloudRain className={`w-5 h-5 ${weatherAlert?.is_active ? 'text-yellow-400' : 'text-gray-400'}`} />
                </div>
                <div>
                  <h3 className="font-orbitron text-sm text-white">Current Weather Alert</h3>
                  {weatherAlert?.is_active ? (
                    <p className="text-yellow-400 text-sm">{weatherAlert.title}</p>
                  ) : (
                    <p className="text-gray-500 text-sm">No active weather alert</p>
                  )}
                </div>
              </div>
              {weatherAlert && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={toggleWeatherAlert}
                    disabled={loading}
                    className={`px-3 py-1.5 rounded-lg font-orbitron text-xs flex items-center gap-2 transition-colors ${
                      weatherAlert.is_active 
                        ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' 
                        : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                    }`}
                  >
                    <Power className="w-3 h-3" />
                    {weatherAlert.is_active ? 'TURN OFF' : 'TURN ON'}
                  </button>
                  <button
                    onClick={deleteWeatherAlert}
                    disabled={loading}
                    className="p-1.5 rounded-lg bg-gray-700 text-gray-400 hover:text-red-400 hover:bg-red-500/20 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Weather Alert Form */}
          <form onSubmit={createWeatherAlert} className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {Object.entries(weatherPresets).map(([type, preset]) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => applyWeatherPreset(type)}
                  className={`p-3 rounded-lg border text-left transition-all hover:scale-105 ${
                    weatherForm.alert_type === type 
                      ? 'bg-neon-blue/20 border-neon-blue text-white' 
                      : 'bg-gray-800/50 border-gray-700 text-gray-400 hover:border-gray-600'
                  }`}
                >
                  <div className="text-lg mb-1">{preset.title.split(' ')[0]}</div>
                  <div className="text-xs font-orbitron truncate">{type.replace('_', ' ')}</div>
                </button>
              ))}
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-400 text-sm mb-1">Alert Title</label>
                <input
                  type="text"
                  value={weatherForm.title}
                  onChange={(e) => setWeatherForm({ ...weatherForm, title: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white focus:border-neon-blue focus:outline-none"
                  placeholder="e.g., PRACTICE CANCELLED"
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-1">Severity</label>
                <Select value={weatherForm.severity} onValueChange={(v) => setWeatherForm({ ...weatherForm, severity: v })}>
                  <SelectTrigger className="bg-gray-800 border-gray-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="info">Info (Blue)</SelectItem>
                    <SelectItem value="warning">Warning (Yellow)</SelectItem>
                    <SelectItem value="danger">Danger (Red)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="block text-gray-400 text-sm mb-1">Message</label>
              <textarea
                value={weatherForm.message}
                onChange={(e) => setWeatherForm({ ...weatherForm, message: e.target.value })}
                className="w-full px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white focus:border-neon-blue focus:outline-none h-20 resize-none"
                placeholder="Detailed message for athletes..."
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg bg-gradient-to-r from-yellow-600 to-orange-600 text-white font-orbitron flex items-center justify-center gap-2 hover:from-yellow-500 hover:to-orange-500 transition-all disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CloudRain className="w-4 h-4" />}
              ACTIVATE WEATHER ALERT
            </button>
          </form>
        </TabsContent>

        {/* SEND CUSTOM ALERT TAB */}
        <TabsContent value="custom" className="space-y-4">
          <form onSubmit={createCustomAlert} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-400 text-sm mb-1">Alert Type</label>
                <Select value={customForm.alert_type} onValueChange={(v) => setCustomForm({ ...customForm, alert_type: v })}>
                  <SelectTrigger className="bg-gray-800 border-gray-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="custom">Custom Announcement</SelectItem>
                    <SelectItem value="account">Account Notice</SelectItem>
                    <SelectItem value="achievement">Achievement</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-1">Display Style</label>
                <Select value={customForm.display_style} onValueChange={(v) => setCustomForm({ ...customForm, display_style: v })}>
                  <SelectTrigger className="bg-gray-800 border-gray-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="toast">Toast (Corner popup)</SelectItem>
                    <SelectItem value="celebration">Celebration (Full screen)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-400 text-sm mb-1">Title</label>
                <input
                  type="text"
                  value={customForm.title}
                  onChange={(e) => setCustomForm({ ...customForm, title: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white focus:border-neon-pink focus:outline-none"
                  placeholder="Alert title..."
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-1">Severity</label>
                <Select value={customForm.severity} onValueChange={(v) => setCustomForm({ ...customForm, severity: v })}>
                  <SelectTrigger className="bg-gray-800 border-gray-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="info">Info (Blue)</SelectItem>
                    <SelectItem value="success">Success (Green)</SelectItem>
                    <SelectItem value="warning">Warning (Yellow)</SelectItem>
                    <SelectItem value="danger">Danger (Red)</SelectItem>
                    <SelectItem value="celebration">Celebration (Pink)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="block text-gray-400 text-sm mb-1">Message</label>
              <textarea
                value={customForm.message}
                onChange={(e) => setCustomForm({ ...customForm, message: e.target.value })}
                className="w-full px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white focus:border-neon-pink focus:outline-none h-20 resize-none"
                placeholder="Your message to athletes..."
              />
            </div>

            <div>
              <label className="block text-gray-400 text-sm mb-1">
                Target Users
                <span className="text-gray-500 ml-2">(Leave empty to send to everyone)</span>
              </label>
              <div className="flex flex-wrap gap-2 p-3 rounded-lg bg-gray-800 border border-gray-700 max-h-32 overflow-y-auto">
                <button
                  type="button"
                  onClick={() => setCustomForm({ ...customForm, target_users: [] })}
                  className={`px-3 py-1 rounded-full text-xs font-orbitron transition-colors ${
                    customForm.target_users.length === 0 
                      ? 'bg-neon-pink/20 text-neon-pink border border-neon-pink/50' 
                      : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                  }`}
                >
                  <Users className="w-3 h-3 inline mr-1" />
                  ALL USERS
                </button>
                {users.slice(0, 20).map((user) => (
                  <button
                    key={user.id}
                    type="button"
                    onClick={() => {
                      const newTargets = customForm.target_users.includes(user.id)
                        ? customForm.target_users.filter(id => id !== user.id)
                        : [...customForm.target_users, user.id];
                      setCustomForm({ ...customForm, target_users: newTargets });
                    }}
                    className={`px-3 py-1 rounded-full text-xs font-rajdhani transition-colors ${
                      customForm.target_users.includes(user.id)
                        ? 'bg-neon-blue/20 text-neon-blue border border-neon-blue/50'
                        : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                    }`}
                  >
                    <User className="w-3 h-3 inline mr-1" />
                    {user.name || user.email?.split('@')[0]}
                  </button>
                ))}
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg bg-gradient-to-r from-neon-pink to-purple-600 text-white font-orbitron flex items-center justify-center gap-2 hover:from-pink-500 hover:to-purple-500 transition-all disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              SEND ALERT
            </button>
          </form>
        </TabsContent>

        {/* ALERT HISTORY TAB */}
        <TabsContent value="history" className="space-y-4">
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {alertHistory.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Bell className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No alerts sent yet</p>
              </div>
            ) : (
              alertHistory.map((alert) => (
                <div 
                  key={alert.id}
                  className="p-4 rounded-lg bg-gray-800/50 border border-gray-700 hover:border-gray-600 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 rounded text-xs font-orbitron border ${severityColors[alert.severity]}`}>
                          {alert.alert_type}
                        </span>
                        <span className="text-gray-500 text-xs">
                          <Clock className="w-3 h-3 inline mr-1" />
                          {formatDate(alert.created_at)}
                        </span>
                      </div>
                      <h4 className="font-orbitron text-sm text-white">{alert.title}</h4>
                      <p className="text-gray-400 text-sm truncate">{alert.message}</p>
                      <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {alert.target_users?.length || 'All'} users
                        </span>
                        <span className="flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          {alert.dismissed_by?.length || 0} dismissed
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => deleteAlert(alert.id)}
                      className="p-2 rounded-lg bg-gray-700 text-gray-400 hover:text-red-400 hover:bg-red-500/20 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AlertsManager;
